import type { KnowledgeStatusEntry } from './knowledge-status-api';
import { formatDate } from './vault-format';

/**
 * Knowledge Store index status chip (SCRUM-495): the per-document indicator
 * in the Vault table's Knowledge column. Five states (AC6):
 *
 * - Not indexed   — hollow neutral dot (backend represents this by ABSENCE:
 *                   `entry` is undefined)
 * - Indexing      — warning-amber dot with an animate-ping halo (live over SSE)
 * - Indexed       — success-emerald dot with a soft glow ring; tooltip carries
 *                   chunk count + last-indexed date
 * - Error         — danger dot; tooltip carries the failure reason
 * - Not indexable — muted slate dash (visually "n/a", NOT a failure); tooltip
 *                   carries the skip reason (URL reference / unsupported
 *                   format / no extractable text)
 *
 * Tooltips use the native `title` attribute — the Vault page's established
 * pattern (the row delete button). The label collapses to dot-only below
 * `sm` so the chip never overflows at narrow widths (responsive rule).
 */
export function KnowledgeStatusChip({
  entry,
}: {
  entry: KnowledgeStatusEntry | undefined;
}): React.JSX.Element {
  if (!entry) {
    return (
      <span
        className="inline-flex items-center gap-1.5"
        title="Not indexed — this document has not been added to the Knowledge Store yet"
        data-testid="knowledge-chip-not-indexed"
      >
        <span className="border-text-3 inline-block h-2 w-2 rounded-full border" />
        <span className="text-text-3 hidden text-[0.6875rem] sm:inline">Not indexed</span>
      </span>
    );
  }

  switch (entry.status) {
    case 'indexing':
      return (
        <span
          className="inline-flex items-center gap-1.5"
          title="Indexing — parsing, chunking, and embedding this document"
          data-testid="knowledge-chip-indexing"
        >
          <span className="relative inline-flex h-2 w-2">
            <span className="bg-warning absolute inline-flex h-full w-full animate-ping rounded-full opacity-60" />
            <span className="bg-warning relative inline-flex h-2 w-2 rounded-full" />
          </span>
          <span className="text-warning hidden text-[0.6875rem] font-medium sm:inline">
            Indexing…
          </span>
        </span>
      );

    case 'indexed':
      return (
        <span
          className="inline-flex items-center gap-1.5"
          title={`Indexed · ${String(entry.chunksWritten)} chunk${entry.chunksWritten === 1 ? '' : 's'} · ${formatDate(entry.updatedAt)}`}
          data-testid="knowledge-chip-indexed"
        >
          <span className="bg-success ring-success/25 inline-block h-2 w-2 rounded-full ring-4" />
          <span className="text-success hidden text-[0.6875rem] font-medium sm:inline">
            Indexed
          </span>
        </span>
      );

    case 'failed':
      return (
        <span
          className="inline-flex items-center gap-1.5"
          title={`Indexing failed${entry.detail ? ` — ${entry.detail}` : ''}`}
          data-testid="knowledge-chip-failed"
        >
          <span className="bg-danger inline-block h-2 w-2 rounded-full" />
          <span className="text-danger hidden text-[0.6875rem] font-medium sm:inline">Error</span>
        </span>
      );

    case 'not_indexable':
      return (
        <span
          className="inline-flex items-center gap-1.5"
          title={`Not indexable${entry.detail ? ` — ${entry.detail}` : ''}`}
          data-testid="knowledge-chip-not-indexable"
        >
          <span className="bg-text-3 inline-block h-0.5 w-2.5 rounded-full" />
          <span className="text-text-3 hidden text-[0.6875rem] sm:inline">Not indexable</span>
        </span>
      );
  }
}
